import {history, historyKeymap} from '@codemirror/commands'
import {EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {spacingControls} from '@github-ui/code-mirror/extensions/spacing-controls'
import {AlphaLabel} from '@github-ui/lifecycle-labels/alpha'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {clsx} from 'clsx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {caretFlashExtension} from './codemirror/extensions/caret-flash'
import {decorateMarkdownExtension} from './codemirror/extensions/decorate-markdown'
import {markdownBehaviorsExtension} from './codemirror/extensions/markdown-behaviors'
import {markdownBehaviorsKeymap} from './codemirror/extensions/markdown-behaviors-keymap'
import {pasteLinksExtension} from './codemirror/extensions/paste-links'
import {singleLineEditorDefaultKeymap, singleLineEditorExtension} from './codemirror/extensions/single-line-editor'
import {useCompartment} from './codemirror/hooks/use-compartment'
import styles from './HybridMarkdownEditor.module.css'
import {Toolbar} from './Toolbar'
import {type MarkdownViewMode, ViewSwitch} from './ViewSwitch'

export interface HybridMarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
  'aria-describedby'?: string
}

export interface HybridMarkdownEditorRef {
  view: EditorView | null
}

export const HybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  function HybridMarkdownEditor(
    {value, onChange, disabled, onChangeViewMode, placeholder: placeholderText, 'aria-describedby': ariaDescribedBy},
    ref,
  ) {
    /** Stable tracking reference to `onChange` so we don't have to update editor extensions when the prop changes. */
    const onChangeRef = useTrackingRef(onChange)
    const onUpdate = useCallback(
      (update: ViewUpdate) => {
        if (update.docChanged) onChangeRef.current(update.state.doc.toString())
      },
      [onChangeRef],
    )

    const [view, setView] = useState<EditorView | null>(null)
    useImperativeHandle(ref, () => ({view}), [view])

    // Each extension that depends on prop values must be contained in a compartment to avoid reinitializing the editor
    // when the props change. Compartments are constant references so safe to use as dependencies.
    const placeholderCompartment = useCompartment(
      view,
      useMemo(() => placeholder(placeholderText ?? ''), [placeholderText]),
    )

    const readonlyCompartment = useCompartment(
      view,
      useMemo(() => (disabled ? readOnly() : []), [disabled]),
    )

    const a11yAttributesCompartment = useCompartment(
      view,
      useMemo(
        () =>
          EditorView.contentAttributes.of({
            'aria-label': 'Markdown content',
            ...(ariaDescribedBy ? {'aria-describedby': ariaDescribedBy} : {}),
          }),
        [ariaDescribedBy],
      ),
    )

    const extensions = useMemo(
      () => [
        singleLineEditorExtension,
        decorateMarkdownExtension,
        caretFlashExtension,
        markdownBehaviorsExtension,
        pasteLinksExtension,
        history(),
        // eslint-disable-next-line react-hooks/refs
        EditorView.updateListener.of(onUpdate),
        a11yAttributesCompartment,
        EditorView.contentAttributes.of({
          // Note: Compartments appear to get applied after first render, which causes a flash of unstyled content when
          // `class` is applied inside a compartment. When we add support for a `className` prop, we'll want to keep
          // this line _in addition_ to the new compartment.
          class: clsx(styles.content, 'markdown-body'),
        }),
        spacingControls({
          indentUnit: 2,
          indentWithTabs: false,
          lineWrapping: true,
        }),
        placeholderCompartment,
        readonlyCompartment,
        keymap.of(markdownBehaviorsKeymap),
        keymap.of(singleLineEditorDefaultKeymap),
        keymap.of(historyKeymap),
      ],
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [onUpdate, placeholderCompartment, readonlyCompartment, a11yAttributesCompartment],
    )

    const containerRef = useRef<HTMLDivElement>(null)

    const initialValueRef = useRef(value)
    useEffect(
      function initializeEditor() {
        const editorView = new EditorView({
          parent: containerRef.current!,
          doc: initialValueRef.current,
          extensions,
        })

        setView(editorView)

        return () => {
          editorView.destroy()
          setView(null)
        }
      },
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [extensions],
    )

    useEffect(function syncValue() {
      if (view?.state.doc.toString() !== value)
        view?.update([view.state.update({changes: {from: 0, to: view.state.doc.length, insert: value}})])
      // Note no dependencies: this runs on every render intentionally. If we had [view, value] as deps, the effect
      // would not run if the user makes a change and then the parent re-renders with the same value (discarding the change).
    })

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Toolbar />

          <div className={styles.viewSwitch}>
            <AlphaLabel className={styles.lifecycleLabel} />
            <ViewSwitch selectedView="hybrid" onViewSelect={onChangeViewMode} />
          </div>
        </div>

        <div ref={containerRef} />
      </div>
    )
  },
)

try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}